import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { LOCAL_TASK_TYPES, PROFILE_MODES } from '@capforge/shared';
import type { ProfileDto, ProfileMode, SolverInfo, UpstreamSettingsDto } from '@capforge/shared';
import { adminFetch, api, errorMessage } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime, formatNumber, formatRatio, formatRelative } from '../lib/format';
import { Table } from '../components/Table';
import type { Column } from '../components/Table';
import { Badge } from '../components/Badge';
import type { BadgeTone } from '../components/Badge';
import { Modal } from '../components/Modal';
import { ErrorBox } from '../components/ErrorBox';
import { Spinner } from '../components/Spinner';
import { Slider } from '../components/Slider';

/**
 * So task toi thieu de duong cong confidence duoc coi la co y nghia.
 *
 * 50 la nguong "mot task khong lam lech uoc tinh qua 2 diem phan tram" (1/50 = 2%).
 * Duoi muc do, buoc slider 0,01 se lam con so nhay lung tung hon ca tac dong that —
 * luc ay hien "chua du du lieu" thay vi ve mot duong cong tu vai diem.
 */
const MIN_CURVE_SAMPLES = 50;

const MODE_LABEL: Record<ProfileMode, string> = {
  off: 'off — bỏ qua solver nội bộ',
  shadow: 'shadow — chỉ đo, vẫn trả đáp án upstream',
  canary: 'canary — dùng đáp án nội bộ cho một phần request',
  live: 'live — ưu tiên đáp án nội bộ',
};

const MODE_TONE: Record<ProfileMode, BadgeTone> = {
  off: 'neutral',
  shadow: 'info',
  canary: 'warn',
  live: 'success',
};

// ---------------------------------------------------------------------------
// Doc stats mot cach phong thu
//
// `stats` do server tinh va dang duoc bo sung song song voi trang nay. Type trong
// @capforge/shared noi cac truong luon co, nhung mot server cu hon (hoac dang trien khai
// do dang) van co the tra thieu. Doc qua `unknown` thay vi tin type: thieu mot truong thi
// hien "chua co du lieu", KHONG duoc vo trang.
// ---------------------------------------------------------------------------

interface CurvePoint {
  threshold: number;
  fallbackRate: number;
}

interface PromotionView {
  eligible: boolean;
  targetMode: ProfileMode | null;
  reasons: string[];
}

interface StatsView {
  present: boolean;
  samples: number | null;
  verifiedSamples: number | null;
  tasks: number | null;
  localSolved: number | null;
  fallbacks: number | null;
  accuracy: number | null;
  shadowTotal: number | null;
  shadowMatchRate: number | null;
  reportedCorrect: number | null;
  reportedIncorrect: number | null;
  curveSampleSize: number | null;
  curve: CurvePoint[];
  promotion: PromotionView | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    if (typeof item === 'string' && item.length > 0) out.push(item);
  }
  return out;
}

function readCurve(value: unknown): CurvePoint[] {
  if (!Array.isArray(value)) return [];
  const out: CurvePoint[] = [];
  for (const raw of value) {
    const obj = asRecord(raw);
    if (obj === null) continue;
    const threshold = asFiniteNumber(obj['threshold']);
    const fallbackRate = asFiniteNumber(obj['fallbackRate']);
    if (threshold === null || fallbackRate === null) continue;
    out.push({ threshold, fallbackRate });
  }
  // Tra cuu va ve duong deu gia dinh nguong tang dan; khong duoc tin thu tu cua server.
  out.sort((a, b) => a.threshold - b.threshold);
  return out;
}

function readPromotion(value: unknown): PromotionView | null {
  const obj = asRecord(value);
  if (obj === null) return null;
  const rawTarget = obj['targetMode'];
  return {
    eligible: obj['eligible'] === true,
    targetMode: PROFILE_MODES.find((mode) => mode === rawTarget) ?? null,
    reasons: asStringList(obj['reasons']),
  };
}

function readStats(profile: ProfileDto): StatsView {
  const raw = asRecord(profile.stats as unknown);
  if (raw === null) {
    return {
      present: false,
      samples: null,
      verifiedSamples: null,
      tasks: null,
      localSolved: null,
      fallbacks: null,
      accuracy: null,
      shadowTotal: null,
      shadowMatchRate: null,
      reportedCorrect: null,
      reportedIncorrect: null,
      curveSampleSize: null,
      curve: [],
      promotion: null,
    };
  }
  const confidence = asRecord(raw['confidence']);
  return {
    present: true,
    samples: asFiniteNumber(raw['samples']),
    verifiedSamples: asFiniteNumber(raw['verifiedSamples']),
    tasks: asFiniteNumber(raw['tasks']),
    localSolved: asFiniteNumber(raw['localSolved']),
    fallbacks: asFiniteNumber(raw['fallbacks']),
    accuracy: asFiniteNumber(raw['accuracy']),
    shadowTotal: asFiniteNumber(raw['shadowTotal']),
    shadowMatchRate: asFiniteNumber(raw['shadowMatchRate']),
    reportedCorrect: asFiniteNumber(raw['reportedCorrect']),
    reportedIncorrect: asFiniteNumber(raw['reportedIncorrect']),
    curveSampleSize: confidence === null ? null : asFiniteNumber(confidence['sampleSize']),
    curve: confidence === null ? [] : readCurve(confidence['curve']),
    promotion: readPromotion(raw['promotion']),
  };
}

// ---------------------------------------------------------------------------
// Uoc tinh ti le fallback
// ---------------------------------------------------------------------------

type FallbackEstimate =
  | { kind: 'ok'; rate: number }
  /** Nguong nam duoi doan server tinh — chi khang dinh duoc can tren, khong biet gia tri that. */
  | { kind: 'upperBound'; rate: number; from: number }
  | { kind: 'noData'; sampleSize: number | null };

function estimateFallback(stats: StatsView, threshold: number): FallbackEstimate {
  const size = stats.curveSampleSize;
  if (stats.curve.length === 0 || size === null || size < MIN_CURVE_SAMPLES) {
    return { kind: 'noData', sampleSize: size };
  }
  const first = stats.curve[0];
  const last = stats.curve[stats.curve.length - 1];
  if (first === undefined || last === undefined) return { kind: 'noData', sampleSize: size };

  if (threshold < first.threshold) {
    // fallbackRate dong bien theo nguong, nen ha nguong xuong duoi doan da tinh chi co the
    // lam ti le fallback nho di. Noi "khong qua X%" la dung; doan mot con so cu the la bia.
    return { kind: 'upperBound', rate: first.fallbackRate, from: first.threshold };
  }
  if (threshold >= last.threshold) return { kind: 'ok', rate: last.fallbackRate };

  // Duong cong la ham bac thang (buoc 0,01 dung bang buoc slider). Lay diem cuoi cung
  // khong vuot qua nguong — noi suy tuyen tinh giua hai bac se sinh ra so khong ton tai.
  let best = first;
  for (const point of stats.curve) {
    if (point.threshold > threshold + 1e-9) break;
    best = point;
  }
  return { kind: 'ok', rate: best.fallbackRate };
}

function estimateRate(estimate: FallbackEstimate): number | null {
  return estimate.kind === 'noData' ? null : estimate.rate;
}

/** Chenh lech tinh bang DIEM phan tram — noi "tang 8,7 diem" thay vi "tang 8,7%" cho khoi mo ho. */
function formatDelta(from: number, to: number): string {
  const delta = (to - from) * 100;
  if (Math.abs(delta) < 0.05) return 'gan nhu khong doi';
  const sign = delta > 0 ? '+' : '−';
  return `${sign}${Math.abs(delta).toFixed(1).replace('.', ',')} diem phan tram`;
}

function formatThreshold(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

/** Circuit breaker con hieu luc hay khong. `disabledUntil` la moc het khoa, khong phai co bat/tat. */
function breakerUntil(profile: ProfileDto): Date | null {
  if (profile.disabledUntil === null) return null;
  const until = new Date(profile.disabledUntil);
  if (Number.isNaN(until.getTime())) return null;
  return until.getTime() > Date.now() ? until : null;
}

/**
 * Dem nguoc toi moc het khoa.
 *
 * Khong dung `formatRelative` cua lib/format: ham do do khoang cach ve QUA KHU, gap moc
 * tuong lai no tra "vua xong". Khoa circuit breaker luon nam o tuong lai.
 */
function formatCountdown(until: Date): string {
  const minutes = Math.round((until.getTime() - Date.now()) / 60_000);
  if (minutes <= 0) return 'sap het';
  if (minutes < 60) return `con ${minutes} phut`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `con ${hours} gio` : `con ${hours} gio ${rest} phut`;
}

function ratioOrNull(part: number | null, total: number | null): number | null {
  if (part === null || total === null || total <= 0) return null;
  return part / total;
}

// ---------------------------------------------------------------------------
// Trang
// ---------------------------------------------------------------------------

interface Draft {
  key: string;
  minConfidence: number;
  canaryPercent: number;
}

export function Profiles() {
  const profiles = useAsync<ProfileDto[]>(
    (signal) => adminFetch<ProfileDto[]>('/admin/profiles', { signal }),
    [],
  );
  const upstream = useAsync<UpstreamSettingsDto>((signal) => api.upstream(signal), []);
  const solvers = useAsync<SolverInfo[]>((signal) => api.solvers(signal), []);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [creating, setCreating] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const rows = profiles.data ?? [];
  const selected = useMemo(
    () => rows.find((row) => row.key === selectedKey) ?? null,
    [rows, selectedKey],
  );

  /**
   * `null` = chua biet (goi /admin/upstream that bai). Chi chan mode shadow khi CHAC CHAN
   * upstream dang tat — khong duoc phat nguoi dung chi vi mot loi mang.
   */
  const upstreamOn: boolean | null = upstream.data === null ? null : upstream.data.enabled;

  const lockedProfiles = useMemo(
    () => rows.filter((row) => breakerUntil(row) !== null),
    [rows],
  );

  const patchProfile = (key: string, body: Record<string, unknown>, onDone?: () => void): void => {
    setBusyKey(key);
    setActionError(null);
    adminFetch<ProfileDto>(`/admin/profiles/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body,
    }).then(
      () => {
        setBusyKey(null);
        if (onDone !== undefined) onDone();
        profiles.reload();
      },
      (err: unknown) => {
        setBusyKey(null);
        setActionError(errorMessage(err));
      },
    );
  };

  const columns: Column<ProfileDto>[] = [
    {
      key: 'profile',
      header: 'Profile',
      render: (row) => (
        <div>
          <div className="cf-mono font-medium text-slate-900 dark:text-slate-100">{row.key}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{row.label}</div>
          {row.matchHosts.length > 0 && (
            <div className="cf-mono mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
              {row.matchHosts.join(', ')}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Loại / solver',
      render: (row) => (
        <div className="space-y-1">
          <div className="cf-mono text-xs">{row.taskType}</div>
          <Badge tone="info">{row.solver}</Badge>
        </div>
      ),
    },
    {
      key: 'mode',
      header: 'Mode',
      className: 'min-w-[15rem]',
      render: (row) => {
        const locked = breakerUntil(row);
        return (
          <div className="space-y-1">
            <ModeSelect
              profile={row}
              upstreamOn={upstreamOn}
              busy={busyKey === row.key}
              onChange={(mode) => patchProfile(row.key, { mode })}
            />
            <div className="flex flex-wrap items-center gap-1">
              {row.mode === 'canary' && (
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  {row.canaryPercent}% request dung dap an noi bo
                </span>
              )}
              {!row.enabled && <Badge tone="neutral">da tat</Badge>}
              {locked !== null && <Badge tone="danger">circuit breaker</Badge>}
            </div>
          </div>
        );
      },
    },
    {
      key: 'samples',
      header: 'Mau',
      className: 'whitespace-nowrap',
      render: (row) => {
        const stats = readStats(row);
        if (!stats.present) return <span className="text-slate-400">chua co du lieu</span>;
        return (
          <div className="text-xs leading-5">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {formatNumber(stats.samples)}
            </div>
            <div className="text-slate-500 dark:text-slate-400">
              {formatNumber(stats.verifiedSamples)} da verified
            </div>
          </div>
        );
      },
    },
    {
      key: 'accuracy',
      header: 'Accuracy',
      className: 'whitespace-nowrap',
      render: (row) => {
        const stats = readStats(row);
        if (!stats.present) return <span className="text-slate-400">—</span>;
        return (
          <div className="text-xs leading-5">
            {stats.accuracy === null ? (
              <span className="text-slate-400" title="Chưa có /reportCorrect hay /reportIncorrect nào">
                chua co bao cao
              </span>
            ) : (
              <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {formatRatio(stats.accuracy)}
              </div>
            )}
            <div className="text-slate-500 dark:text-slate-400">
              {stats.shadowMatchRate === null
                ? 'shadow: chua co mau'
                : `shadow khop ${formatRatio(stats.shadowMatchRate)}`}
            </div>
          </div>
        );
      },
    },
    {
      key: 'fallback',
      header: 'Tỉ lệ fallback',
      className: 'whitespace-nowrap',
      render: (row) => {
        const stats = readStats(row);
        // Mau so la so task DA GIAI XONG (noi bo + fallback), khong phai tong task: task
        // that bai hay dang chay chua tra loi duoc cau hoi "bao nhieu phan tram roi xuong
        // upstream". Lay dung mau so nay thi con so o day moi so sanh duoc voi duong cong
        // uoc tinh o khung tinh chinh — hai so lech mau so la nguoi doc mat tin ngay.
        const settled =
          stats.localSolved === null || stats.fallbacks === null
            ? null
            : stats.localSolved + stats.fallbacks;
        const rate = ratioOrNull(stats.fallbacks, settled);
        return (
          <div className="text-xs leading-5">
            {rate === null ? (
              <span className="text-slate-400">chua co task giai xong</span>
            ) : (
              <div
                className="text-sm font-medium text-slate-900 dark:text-slate-100"
                title="fallback / (giải nội bộ + fallback). Task thất bại và đang chạy không tính."
              >
                {formatRatio(rate)}
              </div>
            )}
            <div className="text-slate-500 dark:text-slate-400">
              {formatNumber(stats.fallbacks)} / {formatNumber(settled)} task
            </div>
          </div>
        );
      },
    },
    {
      key: 'threshold',
      header: 'minConfidence',
      className: 'whitespace-nowrap cf-mono',
      render: (row) => formatThreshold(row.minConfidence),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right whitespace-nowrap',
      render: (row) => (
        <button
          type="button"
          className="cf-btn cf-btn-ghost"
          onClick={() => {
            setActionError(null);
            setDraft(null);
            setSelectedKey((prev) => (prev === row.key ? null : row.key));
          }}
        >
          {selectedKey === row.key ? 'Đóng' : 'Tinh chỉnh'}
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Profile</h1>
          <p className="max-w-3xl text-xs text-slate-500 dark:text-slate-400">
            Moi profile la mot loai captcha cua mot site. Them site moi la them mot dong o day,
            khong phai sua code — do la thu khien capforge dung lai duoc cho project sau.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="cf-btn cf-btn-ghost" onClick={() => profiles.reload()}>
            Lam moi
          </button>
          <button type="button" className="cf-btn cf-btn-primary" onClick={() => setCreating(true)}>
            Tao profile
          </button>
        </div>
      </div>

      {lockedProfiles.length > 0 && <BreakerBanner profiles={lockedProfiles} />}

      {upstreamOn === false && <ShadowUnavailableBanner />}

      {actionError !== null && <ErrorBox message={actionError} title="Thao tác thất bại" />}

      <section className="cf-card">
        <Table
          columns={columns}
          rows={rows}
          rowKey={(row) => row.key}
          loading={profiles.loading}
          error={profiles.error}
          onRetry={profiles.reload}
          isActive={(row) => row.key === selectedKey}
          emptyMessage="Chưa có profile nào"
          emptyHint="Tạo profile đầu tiên rồi để mode ở off — bật live ngay cho một profile chưa đo bao giờ là cach nhanh nhat de tra dap an sai."
        />
      </section>

      {selected !== null && (
        <ProfileDetail
          profile={selected}
          draft={draft !== null && draft.key === selected.key ? draft : null}
          busy={busyKey === selected.key}
          upstreamOn={upstreamOn}
          onDraft={setDraft}
          onSave={(body) => patchProfile(selected.key, body, () => setDraft(null))}
          onClose={() => {
            setSelectedKey(null);
            setDraft(null);
          }}
        />
      )}

      <CreateProfileModal
        open={creating}
        solvers={solvers.data}
        upstreamOn={upstreamOn}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          setCreating(false);
          setSelectedKey(created.key);
          setDraft(null);
          profiles.reload();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Banner
// ---------------------------------------------------------------------------

function BreakerBanner({ profiles }: { profiles: ReadonlyArray<ProfileDto> }) {
  return (
    <div
      role="alert"
      className="rounded-md border-2 border-red-400 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
    >
      <p className="font-semibold">
        Circuit breaker dang khoa {profiles.length} profile
      </p>
      <ul className="mt-2 space-y-1">
        {profiles.map((profile) => {
          const until = breakerUntil(profile);
          return (
            <li key={profile.key}>
              <code className="cf-mono">{profile.key}</code> — khoa den{' '}
              <strong>{formatDateTime(profile.disabledUntil)}</strong>
              {until !== null && (
                <span className="text-red-700 dark:text-red-300"> ({formatCountdown(until)})</span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="mt-2">
        Nguyen nhan: qua nhieu bao cao sai lien tiep. Trong thoi gian khoa, solver noi bo bi bo
        qua va request di theo nhanh fallback. Khoa tu het han — hay dung khoang thoi gian nay de
        xem lai mau va nhan cua profile thay vi mo lai ngay.
      </p>
    </div>
  );
}

function ShadowUnavailableBanner() {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
      <p className="font-semibold">Upstream đang TẮT — mode shadow không dùng được</p>
      <p className="mt-1">
        Shadow chi co nghia khi co dap an cua upstream de doi chieu; khong co upstream thi no
        khong do duoc gi ca. De danh gia solver ma khong ton tien: gan nhan tay mot tap mau o
        trang <strong>Mau</strong>, tach rieng mot tap kiem thu offline (khong dung de train), roi
        dua vao do lam dieu kien thang cap. Dieu kien thang cap o duoi se tu chuyen sang dem bao
        cao tu <code className="cf-mono">/reportCorrect</code>.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dropdown mode
// ---------------------------------------------------------------------------

interface ModeSelectProps {
  profile: ProfileDto;
  upstreamOn: boolean | null;
  busy: boolean;
  onChange: (mode: ProfileMode) => void;
}

function ModeSelect({ profile, upstreamOn, busy, onChange }: ModeSelectProps) {
  const shadowBlocked = upstreamOn === false;
  return (
    <div className="flex items-center gap-2">
      <select
        className="cf-select"
        aria-label={`Mode cua profile ${profile.key}`}
        value={profile.mode}
        disabled={busy}
        onChange={(event) => {
          const value = event.target.value;
          const mode = PROFILE_MODES.find((m) => m === value);
          if (mode !== undefined && mode !== profile.mode) onChange(mode);
        }}
      >
        {PROFILE_MODES.map((mode) => (
          <option
            key={mode}
            value={mode}
            // Van giu option dang duoc chon du bi chan, neu khong o select se trong tron.
            disabled={mode === 'shadow' && shadowBlocked && profile.mode !== 'shadow'}
          >
            {MODE_LABEL[mode]}
            {mode === 'shadow' && shadowBlocked ? ' (cần upstream)' : ''}
          </option>
        ))}
      </select>
      {busy && <Spinner />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bang chi tiet mot profile
// ---------------------------------------------------------------------------

interface ProfileDetailProps {
  profile: ProfileDto;
  draft: Draft | null;
  busy: boolean;
  upstreamOn: boolean | null;
  onDraft: (draft: Draft) => void;
  onSave: (body: Record<string, unknown>) => void;
  onClose: () => void;
}

function ProfileDetail({
  profile,
  draft,
  busy,
  upstreamOn,
  onDraft,
  onSave,
  onClose,
}: ProfileDetailProps) {
  const stats = readStats(profile);
  const minConfidence = draft?.minConfidence ?? profile.minConfidence;
  const canaryPercent = draft?.canaryPercent ?? profile.canaryPercent;
  const dirty =
    minConfidence !== profile.minConfidence || canaryPercent !== profile.canaryPercent;

  const savedEstimate = estimateFallback(stats, profile.minConfidence);
  const draftEstimate = estimateFallback(stats, minConfidence);
  const savedRate = estimateRate(savedEstimate);
  const draftRate = estimateRate(draftEstimate);
  const locked = breakerUntil(profile);

  const setDraftValue = (patch: Partial<Omit<Draft, 'key'>>): void => {
    onDraft({
      key: profile.key,
      minConfidence: patch.minConfidence ?? minConfidence,
      canaryPercent: patch.canaryPercent ?? canaryPercent,
    });
  };

  return (
    <section className="cf-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="cf-mono text-base font-semibold text-slate-900 dark:text-slate-100">
            {profile.key}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {profile.label} · <Badge tone={MODE_TONE[profile.mode]}>{profile.mode}</Badge>
          </p>
        </div>
        <button type="button" className="cf-btn cf-btn-ghost" onClick={onClose}>
          Dong
        </button>
      </div>

      {locked !== null && (
        <div
          role="alert"
          className="mt-3 rounded-md border-2 border-red-400 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
        >
          Profile nay dang bi circuit breaker khoa den{' '}
          <strong>{formatDateTime(profile.disabledUntil)}</strong>. Doi mode bay gio khong lam
          solver chay lai truoc thoi diem do.
        </div>
      )}

      <div className="mt-4 grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <Slider
            id={`min-confidence-${profile.key}`}
            label="minConfidence"
            value={minConfidence}
            min={0}
            max={1}
            step={0.01}
            display={formatThreshold(minConfidence)}
            minLabel="0,00 — nhận mọi đáp án"
            maxLabel="1,00 — chỉ nhận chắc chắn"
            disabled={busy}
            onChange={(value) => {
              // Lam tron ve 2 chu so: dinh dang so cua input range co the tra 0.9300000000000001,
              // va con so do se di thang vao body PATCH neu khong chan o day.
              setDraftValue({ minConfidence: Math.round(value * 100) / 100 });
            }}
            hint={
              <FallbackImpact
                stats={stats}
                savedThreshold={profile.minConfidence}
                draftThreshold={minConfidence}
                savedEstimate={savedEstimate}
                draftEstimate={draftEstimate}
              />
            }
          />

          {stats.curve.length > 0 &&
            stats.curveSampleSize !== null &&
            stats.curveSampleSize >= MIN_CURVE_SAMPLES && (
              <FallbackCurveChart
                curve={stats.curve}
                draftThreshold={minConfidence}
                draftRate={draftRate}
                savedThreshold={profile.minConfidence}
                savedRate={savedRate}
              />
            )}

          <Slider
            id={`canary-percent-${profile.key}`}
            label="canaryPercent"
            value={canaryPercent}
            min={0}
            max={100}
            step={1}
            display={`${canaryPercent}%`}
            minLabel="0%"
            maxLabel="100%"
            disabled={busy || profile.mode !== 'canary'}
            onChange={(value) => setDraftValue({ canaryPercent: Math.round(value) })}
            hint={
              profile.mode === 'canary'
                ? `${canaryPercent}% request se dung dap an noi bo, phan con lai di theo duong cu.`
                : 'Chỉ chỉnh được khi mode = canary. Ở mode khác con số này không ảnh hưởng gì cả nên để khoá cho khỏi hieu nham.'
            }
          />

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="cf-btn cf-btn-primary"
              disabled={!dirty || busy}
              onClick={() => {
                const body: Record<string, unknown> = {};
                if (minConfidence !== profile.minConfidence) body['minConfidence'] = minConfidence;
                if (canaryPercent !== profile.canaryPercent) body['canaryPercent'] = canaryPercent;
                onSave(body);
              }}
            >
              {busy ? <Spinner label="Đang lưu" /> : 'Lưu thay đổi'}
            </button>
            {dirty && (
              <button
                type="button"
                className="cf-btn cf-btn-ghost"
                disabled={busy}
                onClick={() =>
                  onDraft({
                    key: profile.key,
                    minConfidence: profile.minConfidence,
                    canaryPercent: profile.canaryPercent,
                  })
                }
              >
                Hoan tac
              </button>
            )}
            {!dirty && (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Chưa có thay đổi nào chưa lưu.
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <PromotionBox
            profile={profile}
            stats={stats}
            upstreamOn={upstreamOn}
            busy={busy}
            onPromote={(mode) => onSave({ mode })}
          />
          <ProfileFacts profile={profile} stats={stats} />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tac dong uoc tinh cua nguong len ti le fallback
// ---------------------------------------------------------------------------

interface FallbackImpactProps {
  stats: StatsView;
  savedThreshold: number;
  draftThreshold: number;
  savedEstimate: FallbackEstimate;
  draftEstimate: FallbackEstimate;
}

function FallbackImpact({
  stats,
  savedThreshold,
  draftThreshold,
  savedEstimate,
  draftEstimate,
}: FallbackImpactProps) {
  if (draftEstimate.kind === 'noData' || savedEstimate.kind === 'noData') {
    const size = stats.curveSampleSize;
    return (
      <span className="text-amber-700 dark:text-amber-300">
        Chua du du lieu de uoc tinh tac dong. Can it nhat {MIN_CURVE_SAMPLES} task da co
        confidence cua chinh profile nay
        {size === null ? '' : `, dang co ${formatNumber(size)}`}. Cho profile chay them (mode
        shadow la an toan nhat) roi quay lai.
      </span>
    );
  }

  const draftText =
    draftEstimate.kind === 'upperBound'
      ? `khong qua ${formatRatio(draftEstimate.rate)}`
      : formatRatio(draftEstimate.rate);
  const savedText =
    savedEstimate.kind === 'upperBound'
      ? `khong qua ${formatRatio(savedEstimate.rate)}`
      : formatRatio(savedEstimate.rate);

  return (
    <span>
      Nguong{' '}
      <strong className="text-slate-700 dark:text-slate-200">
        {formatThreshold(draftThreshold)}
      </strong>{' '}
      → ti le fallback uoc tinh{' '}
      <strong className="text-slate-900 dark:text-slate-100">{draftText}</strong>
      {draftThreshold !== savedThreshold && (
        <>
          {' '}
          (dang luu {formatThreshold(savedThreshold)}: {savedText}; thay doi{' '}
          {formatDelta(savedEstimate.rate, draftEstimate.rate)})
        </>
      )}
      .{' '}
      <span className="text-slate-400 dark:text-slate-500">
        Tinh tu {formatNumber(stats.curveSampleSize)} task gan nhat cua profile nay
        {draftEstimate.kind === 'upperBound'
          ? `; server chi tinh duong cong tu ${formatThreshold(draftEstimate.from)} tro len`
          : ''}
        .
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Duong cong fallback
//
// Mot chuoi so lieu duy nhat (ti le fallback theo nguong) nen khong can chu giai — tieu de
// da goi ten no. Mau lay tu bien --viz-* dung chung voi bieu do trang Tong quan, de ca
// dashboard chi co mot bang mau. Diem dang keo duoc danh nhan truc tiep; khong ghi so len
// moi diem vi 51 nhan chong nhau thi khong doc duoc gi.
// ---------------------------------------------------------------------------

const CHART_W = 320;
const CHART_H = 120;
const CHART_PAD = { top: 10, right: 12, bottom: 18, left: 38 } as const;

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

interface FallbackCurveChartProps {
  curve: CurvePoint[];
  draftThreshold: number;
  draftRate: number | null;
  savedThreshold: number;
  savedRate: number | null;
}

function FallbackCurveChart({
  curve,
  draftThreshold,
  draftRate,
  savedThreshold,
  savedRate,
}: FallbackCurveChartProps) {
  const first = curve[0];
  const last = curve[curve.length - 1];
  if (first === undefined || last === undefined) return null;
  const tMin = first.threshold;
  const tMax = last.threshold;
  const span = tMax - tMin;
  if (span <= 0) return null;

  const plotW = CHART_W - CHART_PAD.left - CHART_PAD.right;
  const plotH = CHART_H - CHART_PAD.top - CHART_PAD.bottom;
  const toX = (t: number): number => CHART_PAD.left + ((clamp(t, tMin, tMax) - tMin) / span) * plotW;
  const toY = (rate: number): number => CHART_PAD.top + (1 - clamp(rate, 0, 1)) * plotH;

  const path = curve
    .map((point, index) => {
      const command = index === 0 ? 'M' : 'L';
      return `${command}${toX(point.threshold).toFixed(1)} ${toY(point.fallbackRate).toFixed(1)}`;
    })
    .join(' ');

  const draftX = toX(draftThreshold);
  // Nhan cua diem dang keo lat sang trai khi diem nam sat mep phai, neu khong chu bi cat.
  const labelAnchor = draftX > CHART_W - 60 ? 'end' : 'start';
  const labelX = labelAnchor === 'end' ? draftX - 6 : draftX + 6;

  return (
    <figure className="m-0">
      <figcaption className="cf-label">Tỉ lệ fallback theo ngưỡng (dữ liệu lịch sử)</figcaption>
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="w-full max-w-md"
        role="img"
        aria-label={`Duong cong ti le fallback theo nguong confidence, tu ${formatThreshold(tMin)} den ${formatThreshold(tMax)}. Tai nguong ${formatThreshold(draftThreshold)} ti le fallback uoc tinh ${draftRate === null ? 'chua ro' : formatRatio(draftRate)}.`}
      >
        {[0, 0.5, 1].map((rate) => (
          <g key={rate}>
            <line
              x1={CHART_PAD.left}
              x2={CHART_W - CHART_PAD.right}
              y1={toY(rate)}
              y2={toY(rate)}
              stroke="var(--viz-grid)"
              strokeWidth={1}
            />
            <text
              x={CHART_PAD.left - 6}
              y={toY(rate) + 3}
              textAnchor="end"
              className="fill-slate-500 text-[9px] dark:fill-slate-400"
            >
              {rate * 100}%
            </text>
          </g>
        ))}

        {[tMin, (tMin + tMax) / 2, tMax].map((t) => (
          <text
            key={t}
            x={toX(t)}
            y={CHART_H - 5}
            textAnchor="middle"
            className="fill-slate-500 text-[9px] dark:fill-slate-400"
          >
            {formatThreshold(t)}
          </text>
        ))}

        {savedRate !== null && savedThreshold !== draftThreshold && (
          <g>
            <line
              x1={toX(savedThreshold)}
              x2={toX(savedThreshold)}
              y1={CHART_PAD.top}
              y2={CHART_H - CHART_PAD.bottom}
              stroke="var(--viz-axis)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <circle
              cx={toX(savedThreshold)}
              cy={toY(savedRate)}
              r={3.5}
              fill="var(--viz-axis)"
              stroke="var(--viz-surface)"
              strokeWidth={2}
            />
          </g>
        )}

        <path d={path} fill="none" stroke="var(--viz-local)" strokeWidth={2} strokeLinejoin="round" />

        <line
          x1={draftX}
          x2={draftX}
          y1={CHART_PAD.top}
          y2={CHART_H - CHART_PAD.bottom}
          stroke="var(--viz-local)"
          strokeWidth={1}
          strokeDasharray="2 2"
        />
        {draftRate !== null && (
          <>
            <circle
              cx={draftX}
              cy={toY(draftRate)}
              r={4.5}
              fill="var(--viz-local)"
              stroke="var(--viz-surface)"
              strokeWidth={2}
            />
            <text
              x={labelX}
              // Diem sat dinh bieu do thi lat nhan xuong duoi, neu khong chu bi cat mat.
              y={toY(draftRate) < CHART_PAD.top + 14 ? toY(draftRate) + 13 : toY(draftRate) - 8}
              textAnchor={labelAnchor}
              className="fill-slate-900 text-[10px] font-semibold dark:fill-slate-100"
            >
              {formatRatio(draftRate)}
            </text>
          </>
        )}
      </svg>
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        Duong dut mau xam: nguong dang luu. Diem dam: nguong dang keo.
      </p>
    </figure>
  );
}

// ---------------------------------------------------------------------------
// Dieu kien thang cap
// ---------------------------------------------------------------------------

interface PromotionBoxProps {
  profile: ProfileDto;
  stats: StatsView;
  upstreamOn: boolean | null;
  busy: boolean;
  onPromote: (mode: ProfileMode) => void;
}

function PromotionBox({ profile, stats, upstreamOn, busy, onPromote }: PromotionBoxProps) {
  const promotion = stats.promotion;

  if (promotion === null) {
    return (
      <Box tone="warn" title="Chưa đánh giá được điều kiện thăng cấp">
        <p>
          Server khong tra khoi <code className="cf-mono">stats.promotion</code> cho profile nay.
          Dieu kien thang cap do server tinh — dashboard khong tu tinh lai de tranh hai noi noi hai
          con so khac nhau. Hay cap nhat server roi tai lai trang.
        </p>
      </Box>
    );
  }

  const target = promotion.targetMode;

  if (target === null) {
    return (
      <Box tone="neutral" title={`Không có bước thăng cấp nào từ mode ${profile.mode}`}>
        {promotion.reasons.length > 0 ? (
          <ReasonList reasons={promotion.reasons} />
        ) : (
          <p>Server không đề xuất mode kế tiếp cho profile này.</p>
        )}
      </Box>
    );
  }

  if (promotion.eligible) {
    return (
      <Box tone="success" title={`Du dieu kien len ${target}`}>
        <p>
          {target === 'shadow'
            ? 'Lên shadow không cần điều kiện: solver chỉ chạy để đo, đáp án trả về vẫn là của upstream. Rủi ro bằngg khong.'
            : 'Các ngưỡng ở mục 11 đặc tả đã đạt. Thăng cấp không bao giờ tự động — nút này luôn do bạn bấm.'}
        </p>
        <button
          type="button"
          className="cf-btn cf-btn-primary mt-2"
          disabled={busy}
          onClick={() => onPromote(target)}
        >
          {busy ? <Spinner label="Đang đợi" /> : `Promote len ${target}`}
        </button>
      </Box>
    );
  }

  return (
    <Box tone="warn" title={`Chua du dieu kien len ${target}`}>
      <ReasonList reasons={promotion.reasons} />
      <button type="button" className="cf-btn cf-btn-primary mt-2" disabled>
        Promote len {target}
      </button>
      <p className="mt-2 text-xs">
        {upstreamOn === false
          ? 'Không có upstream nên điều kiện dựa vào báo cáo thật từ client: SDK phải gọi report() sau khi biết ket qua submit.'
          : 'Vẫn có thể đổi mode bằng dropdown ở bảng trên nếu bạn chấp nhận rủi ro — nút này chỉ là đường an toàn.'}
      </p>
    </Box>
  );
}

function ReasonList({ reasons }: { reasons: ReadonlyArray<string> }) {
  if (reasons.length === 0) {
    return <p>Server không nói rõ còn thiếu gì.</p>;
  }
  return (
    <ul className="list-disc space-y-1 pl-5">
      {reasons.map((reason) => (
        <li key={reason}>{reason}</li>
      ))}
    </ul>
  );
}

const BOX_TONE: Record<'neutral' | 'success' | 'warn', string> = {
  neutral:
    'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
  success:
    'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  warn: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
};

function Box({
  tone,
  title,
  children,
}: {
  tone: 'neutral' | 'success' | 'warn';
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={`rounded-md border p-3 text-sm ${BOX_TONE[tone]}`}>
      <p className="font-semibold">{title}</p>
      <div className="mt-1 space-y-1">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thong tin phu
// ---------------------------------------------------------------------------

function ProfileFacts({ profile, stats }: { profile: ProfileDto; stats: StatsView }) {
  const hints = Object.keys(profile.matchHints);
  return (
    <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 text-xs">
      <dt className="text-slate-500 dark:text-slate-400">taskType</dt>
      <dd className="cf-mono">{profile.taskType}</dd>
      <dt className="text-slate-500 dark:text-slate-400">solver</dt>
      <dd className="cf-mono">{profile.solver}</dd>
      <dt className="text-slate-500 dark:text-slate-400">matchHosts</dt>
      <dd className="cf-mono break-words">
        {profile.matchHosts.length === 0 ? '—' : profile.matchHosts.join(', ')}
      </dd>
      <dt className="text-slate-500 dark:text-slate-400">matchHints</dt>
      <dd className="cf-mono break-words">{hints.length === 0 ? '—' : hints.join(', ')}</dd>
      <dt className="text-slate-500 dark:text-slate-400">Task đã chạy</dt>
      <dd>
        {formatNumber(stats.tasks)} · noi bo {formatNumber(stats.localSolved)} · fallback{' '}
        {formatNumber(stats.fallbacks)}
      </dd>
      <dt className="text-slate-500 dark:text-slate-400">Báo cáo</dt>
      <dd>
        dung {formatNumber(stats.reportedCorrect)} · sai {formatNumber(stats.reportedIncorrect)}
      </dd>
      <dt className="text-slate-500 dark:text-slate-400">Shadow</dt>
      <dd>
        {stats.shadowTotal === null || stats.shadowTotal === 0
          ? 'chua co mau doi chieu'
          : `${formatNumber(stats.shadowTotal)} mau · khop ${formatRatio(stats.shadowMatchRate)}`}
      </dd>
      <dt className="text-slate-500 dark:text-slate-400">Cập nhật</dt>
      <dd title={formatDateTime(profile.updatedAt)}>{formatRelative(profile.updatedAt)}</dd>
    </dl>
  );
}

// ---------------------------------------------------------------------------
// Tao profile
// ---------------------------------------------------------------------------

/** Dong bo voi ProfileInputSchema o @capforge/shared — bao loi tai cho thay vi doi HTTP 400. */
const KEY_PATTERN = /^[a-z0-9][a-z0-9._-]*$/;

interface CreateProfileModalProps {
  open: boolean;
  solvers: SolverInfo[] | null;
  upstreamOn: boolean | null;
  onClose: () => void;
  onCreated: (profile: ProfileDto) => void;
}

function CreateProfileModal({
  open,
  solvers,
  upstreamOn,
  onClose,
  onCreated,
}: CreateProfileModalProps) {
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [taskType, setTaskType] = useState<string>('TextCaptchaTask');
  const [solver, setSolver] = useState('');
  const [hosts, setHosts] = useState('');
  const [mode, setMode] = useState<ProfileMode>('off');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const solverList = solvers ?? [];
  const chosenSolver = solverList.find((item) => item.name === solver) ?? null;
  // Chi canh bao, khong chan: solver co the nhan them task type ma registry chua khai bao.
  const solverMismatch =
    chosenSolver !== null && !chosenSolver.taskTypes.includes(taskType) ? chosenSolver : null;

  const reset = (): void => {
    setKey('');
    setLabel('');
    setTaskType('TextCaptchaTask');
    setSolver('');
    setHosts('');
    setMode('off');
    setError(null);
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const submit = (): void => {
    const trimmedKey = key.trim();
    if (!KEY_PATTERN.test(trimmedKey) || trimmedKey.length > 80) {
      setError('Key chỉ cho phép chữ thường, số và dấu . _ - ; bắt đầu bằng chữ hoặc số.');
      return;
    }
    const trimmedLabel = label.trim();
    if (trimmedLabel.length === 0) {
      setError('Hãy đặt tên để sau này nhìn là biết profile của site nào.');
      return;
    }
    const trimmedSolver = solver.trim();
    if (trimmedSolver.length === 0) {
      setError('Phải chọn solver.');
      return;
    }

    // Tach theo dau phay VA khoang trang: nguoi ta dan danh sach host tu nhieu nguon,
    // moi nguon mot kieu ngan cach.
    const matchHosts = hosts
      .split(/[\s,]+/)
      .map((host) => host.trim().toLowerCase())
      .filter((host) => host.length > 0);

    setSaving(true);
    setError(null);
    adminFetch<ProfileDto>('/admin/profiles', {
      method: 'POST',
      body: {
        key: trimmedKey,
        label: trimmedLabel,
        taskType,
        solver: trimmedSolver,
        matchHosts,
        mode,
      },
    }).then(
      (created) => {
        setSaving(false);
        reset();
        onCreated(created);
      },
      (err: unknown) => {
        setSaving(false);
        setError(errorMessage(err));
      },
    );
  };

  return (
    <Modal
      open={open}
      title="Tạo profile mới"
      onClose={close}
      footer={
        <>
          <button type="button" className="cf-btn cf-btn-ghost" onClick={close}>
            Huy
          </button>
          <button
            type="button"
            className="cf-btn cf-btn-primary"
            onClick={submit}
            disabled={saving}
          >
            {saving ? <Spinner label="Đang tạo" /> : 'Tạo profile'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="cf-label" htmlFor="profile-key">
            Key
          </label>
          <input
            id="profile-key"
            className="cf-input cf-mono"
            value={key}
            maxLength={80}
            placeholder="vd: securimage-default"
            onChange={(event) => setKey(event.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Client co the gui thang <code className="cf-mono">task.capforge.profileKey</code> bang
            chuoi nay de chi dinh profile, nen dat ten on dinh ngay tu dau.
          </p>
        </div>

        <div>
          <label className="cf-label" htmlFor="profile-label">
            Ten hien thi
          </label>
          <input
            id="profile-label"
            className="cf-input"
            value={label}
            maxLength={160}
            placeholder="vd: Securimage mặc định"
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="cf-label" htmlFor="profile-tasktype">
              taskType
            </label>
            <select
              id="profile-tasktype"
              className="cf-select"
              value={taskType}
              onChange={(event) => setTaskType(event.target.value)}
            >
              {LOCAL_TASK_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="cf-label" htmlFor="profile-solver">
              Solver
            </label>
            {solverList.length > 0 ? (
              <select
                id="profile-solver"
                className="cf-select"
                value={solver}
                onChange={(event) => setSolver(event.target.value)}
              >
                <option value="">— chon solver —</option>
                {solverList.map((item) => (
                  <option key={item.name} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            ) : (
              // Khong goi duoc /admin/solvers thi van phai tao duoc profile — go tay.
              <input
                id="profile-solver"
                className="cf-input cf-mono"
                value={solver}
                placeholder="vd: text"
                onChange={(event) => setSolver(event.target.value)}
              />
            )}
          </div>
        </div>

        {solverMismatch !== null && (
          <p className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            Solver <code className="cf-mono">{solverMismatch.name}</code> khai bao nhan{' '}
            {solverMismatch.taskTypes.join(', ')} — khong co <code className="cf-mono">{taskType}</code>.
            Van tao duoc, nhung dispatcher se bo qua solver nay va request se di thang xuong
            fallback.
          </p>
        )}

        <div>
          <label className="cf-label" htmlFor="profile-hosts">
            matchHosts
          </label>
          <textarea
            id="profile-hosts"
            className="cf-input h-20"
            value={hosts}
            placeholder="example.com, *.example.org"
            onChange={(event) => setHosts(event.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Cach nhau bang dau phay hoac xuong dong. De trong cung duoc: luc do profile chi duoc
            chon khi client gui thang profileKey.
          </p>
        </div>

        <div>
          <label className="cf-label" htmlFor="profile-mode">
            Mode ban dau
          </label>
          <select
            id="profile-mode"
            className="cf-select"
            value={mode}
            onChange={(event) => {
              const value = event.target.value;
              const found = PROFILE_MODES.find((m) => m === value);
              if (found !== undefined) setMode(found);
            }}
          >
            {PROFILE_MODES.map((m) => (
              <option key={m} value={m} disabled={m === 'shadow' && upstreamOn === false}>
                {MODE_LABEL[m]}
                {m === 'shadow' && upstreamOn === false ? ' (cần upstream)' : ''}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Bat dau tu <code className="cf-mono">off</code> roi thang cap dan. Profile chua do bao
            gio ma dat thang <code className="cf-mono">live</code> la cach nhanh nhat de tra dap an
            sai cho client that.
          </p>
        </div>

        {error !== null && (
          <p className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
